import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { demoSteps, pantryItems, recipes } from '../data/mockData';

const { 
  FiPlay, FiCheck, FiArrowRight, FiLayers, FiTerminal, 
  FiDatabase, FiHelpCircle, FiChevronRight, FiClock, FiActivity 
} = FiIcons;

const StepCard = ({ step, index, currentStep, setStep }) => {
  const isActive = currentStep === index;
  const isCompleted = currentStep > index;

  return (
    <div 
      onClick={() => setStep(index)}
      className={`relative pl-10 pb-10 cursor-pointer group last:pb-0`}
    >
      {index !== demoSteps.length - 1 && (
        <div className={`absolute left-[13px] top-8 w-0.5 h-full transition-colors duration-500 ${isCompleted ? 'bg-tomato-500' : 'bg-steel-200'}`} />
      )}
      
      <div className={`absolute left-0 top-1 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-500 z-10 ${
        isActive ? 'bg-tomato-500 border-tomato-500 scale-110 shadow-[0_0_12px_rgba(230,57,70,0.4)]' : 
        isCompleted ? 'bg-white border-tomato-500' : 'bg-white border-steel-300'
      }`}>
        {isCompleted ? (
          <SafeIcon icon={FiCheck} className="text-tomato-500 text-sm" />
        ) : (
          <span className={`text-xs font-bold ${isActive ? 'text-white' : 'text-steel-400'}`}>{index + 1}</span>
        )}
      </div>

      <div className={`transition-all duration-300 ${isActive ? 'translate-x-2' : ''}`}>
        <h4 className={`text-sm font-bold uppercase tracking-widest ${isActive ? 'text-tomato-500' : 'text-steel-800'}`}>
          {step.title}
        </h4>
        <p className={`text-xs mt-1 max-w-sm leading-relaxed ${isActive ? 'text-steel-900' : 'text-steel-400'}`}>
          {step.desc}
        </p>
        
        <AnimatePresence>
          {isActive && (
            <motion.button
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 px-4 py-2 bg-steel-900 text-white text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-tomato-500 transition-colors flex items-center gap-2"
            >
              {step.action} <SafeIcon icon={FiArrowRight} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const StateLabel = ({ type, text }) => {
  const styles = {
    success: 'bg-green-100 text-green-700 border-green-200',
    warning: 'bg-orange-100 text-orange-700 border-orange-200',
    error: 'bg-red-100 text-red-700 border-red-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
    empty: 'bg-steel-100 text-steel-500 border-steel-200',
    progress: 'bg-tomato-50 text-tomato-600 border-tomato-100',
  };
  return (
    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase border ${styles[type]}`}>
      {text}
    </span>
  );
};

export const Demo = () => {
  const [currentStep, setCurrentStep] = useState(0);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-12 pb-24"
    >
      <header className="border-b border-steel-200 pb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-tomato-500 rounded text-white shadow-lg shadow-tomato-500/20">
              <SafeIcon icon={FiPlay} />
            </div>
            <h1 className="text-3xl font-bold text-steel-900 tracking-tight">Product Demo</h1>
          </div>
          <p className="text-steel-600 leading-relaxed">
            Experience the <strong>CookCircuit</strong> logic flow. Our system automates the cognitive effort 
            of household meal management by treating your kitchen as an integrated circuit.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-steel-200 text-xs font-bold uppercase tracking-widest bg-white rounded hover:bg-steel-50 transition-colors flex items-center gap-2">
            <SafeIcon icon={FiHelpCircle} /> Help Docs
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Workflow Section */}
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2 mb-6 text-steel-900 font-bold uppercase tracking-[0.2em] text-[10px]">
            <SafeIcon icon={FiTerminal} className="text-tomato-500" />
            Try This Flow
          </div>
          <div className="panel p-8 bg-white border-steel-200 shadow-sm">
            {demoSteps.map((step, idx) => (
              <StepCard 
                key={step.id} 
                step={step} 
                index={idx} 
                currentStep={currentStep} 
                setStep={setCurrentStep} 
              />
            ))}
          </div>
          
          <div className="mt-8 p-5 bg-steel-900 rounded-sm text-cream-100 font-mono text-[10px] uppercase tracking-widest leading-loose border-l-4 border-tomato-500">
            <div className="text-tomato-400 mb-2 flex items-center justify-between">
              <span>// Command Line Output</span>
              <span className="w-2 h-2 rounded-full bg-tomato-500 animate-pulse"></span>
            </div>
            <div className="opacity-80">
              {currentStep === 0 && "> Scanning 142 storage units...\n> 6 Critical stock levels detected.\n> Inventory matrix updated."}
              {currentStep === 1 && "> Preference: High Protein / Low Carb.\n> Appliance check: Smart Oven Online.\n> Filtering 142 recipes... 12 found."}
              {currentStep === 2 && "> Optimizing 7-day circuit...\n> Co-prep batching enabled.\n> Board generated (Eff: 94%)."}
              {currentStep === 3 && "> Cross-referencing pantry vs board...\n> 8 missing items identified.\n> Staging Grocery Hub order."}
              {currentStep === 4 && "> Syncing Thermal Profile: 400°F.\n> Prep module 1/3 active.\n> Waiting for user insert."}
            </div>
          </div>
        </div>

        {/* Sample Data & States Section */}
        <div className="lg:col-span-8 space-y-12">
          {/* Sample Data Area */}
          <section>
            <div className="flex items-center gap-2 mb-6 text-steel-900 font-bold uppercase tracking-[0.2em] text-[10px]">
              <SafeIcon icon={FiDatabase} className="text-tomato-500" />
              Sample Modular Data
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="panel bg-white border-steel-200">
                <div className="panel-header text-[9px] tracking-[0.2em]">Module Specs // Nutritional</div>
                <div className="p-6">
                  <div className="border-[3px] border-steel-900 p-4 font-bold font-mono text-steel-900">
                    <div className="text-3xl border-b-[10px] border-steel-900 pb-1 mb-2 tracking-tighter">Circuit Specs</div>
                    <div className="flex justify-between border-b-2 border-steel-900 py-1 text-sm">
                      <span>Serving Size</span>
                      <span>1 Module</span>
                    </div>
                    <div className="text-4xl border-b-8 border-steel-900 py-1 mb-1 font-black">450 Cals</div>
                    <div className="flex justify-between border-b border-steel-300 py-2 text-sm">
                      <span className="flex items-center gap-1">Total Fat <span className="font-normal opacity-50">28g</span></span>
                      <span>36%</span>
                    </div>
                    <div className="flex justify-between border-b border-steel-300 py-2 text-sm">
                      <span className="flex items-center gap-1">Total Carbs <span className="font-normal opacity-50">5g</span></span>
                      <span>2%</span>
                    </div>
                    <div className="flex justify-between py-2 text-sm">
                      <span className="flex items-center gap-1">Protein <span className="font-black">35g</span></span>
                      <span className="font-black">70%</span>
                    </div>
                    <div className="text-[8px] font-normal leading-tight mt-4 opacity-70">
                      * Values are simulated based on standard ingredient profiles. No specific health claims intended.
                    </div>
                  </div>
                </div>
              </div>

              <div className="panel bg-white border-steel-200 flex flex-col">
                <div className="panel-header text-[9px] tracking-[0.2em]">Logic Grid // Appliance Sync</div>
                <div className="p-6 flex-1 flex flex-col justify-center gap-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-steel-100 rounded group-hover:bg-tomato-50 transition-colors">
                          <SafeIcon icon={FiClock} className="text-steel-600 group-hover:text-tomato-500" />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-widest">Pre-heat Buffer</span>
                      </div>
                      <span className="font-mono text-tomato-500 font-bold">08:00</span>
                    </div>
                    <div className="flex justify-between items-center group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-steel-100 rounded group-hover:bg-tomato-50 transition-colors">
                          <SafeIcon icon={FiLayers} className="text-steel-600 group-hover:text-tomato-500" />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-widest">Thermal Resting</span>
                      </div>
                      <span className="font-mono text-tomato-500 font-bold">05:00</span>
                    </div>
                    <div className="flex justify-between items-center group">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-steel-100 rounded group-hover:bg-tomato-50 transition-colors">
                          <SafeIcon icon={FiActivity} className="text-steel-600 group-hover:text-tomato-500" />
                        </div>
                        <span className="text-xs font-bold uppercase tracking-widest">Efficiency Rating</span>
                      </div>
                      <span className="font-mono text-green-600 font-bold">94%</span>
                    </div>
                  </div>
                  <div className="h-px bg-steel-100"></div>
                  <div className="bg-steel-50 p-4 rounded border border-steel-100">
                    <p className="text-[10px] text-steel-500 uppercase tracking-widest mb-2 font-bold">Active Module Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {['Oven', 'Induction', 'Family-Size', 'Low-Waste'].map(t => (
                        <span key={t} className="px-2 py-1 bg-white border border-steel-200 text-[9px] font-mono rounded text-steel-700">#{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Simulated States Area */}
          <section>
            <div className="flex items-center gap-2 mb-6 text-steel-900 font-bold uppercase tracking-[0.2em] text-[10px]">
              <SafeIcon icon={FiLayers} className="text-tomato-500" />
              Simulated UI States
            </div>
            
            <div className="panel bg-white border-steel-200 overflow-hidden shadow-sm">
               <table className="w-full text-left text-[11px]">
                 <thead className="bg-steel-900 text-white uppercase font-mono text-[9px] tracking-widest">
                   <tr>
                     <th className="px-6 py-4 font-medium">Component Module</th>
                     <th className="px-6 py-4 font-medium">System Context</th>
                     <th className="px-6 py-4 font-medium">State Label</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-steel-100">
                   <tr className="hover:bg-steel-50 transition-colors">
                     <td className="px-6 py-5 font-bold text-steel-900">Pantry Tracker</td>
                     <td className="px-6 py-5 text-steel-500 italic">"Stock level at 0% detected..."</td>
                     <td className="px-6 py-5"><StateLabel type="empty" text="Empty" /></td>
                   </tr>
                   <tr className="hover:bg-steel-50 transition-colors">
                     <td className="px-6 py-5 font-bold text-steel-900">Appliance Hub</td>
                     <td className="px-6 py-5 text-steel-500 italic">"Thermal spike in Smart Oven X1..."</td>
                     <td className="px-6 py-5"><StateLabel type="warning" text="Warning" /></td>
                   </tr>
                   <tr className="hover:bg-steel-50 transition-colors">
                     <td className="px-6 py-5 font-bold text-steel-900">Recipe Module</td>
                     <td className="px-6 py-5 text-steel-500 italic">"Cycle 42 execution complete..."</td>
                     <td className="px-6 py-5"><StateLabel type="success" text="Success" /></td>
                   </tr>
                   <tr className="hover:bg-steel-50 transition-colors">
                     <td className="px-6 py-5 font-bold text-steel-900">Prep Timer</td>
                     <td className="px-6 py-5 text-steel-500 italic">"Phase 3: Searing in progress..."</td>
                     <td className="px-6 py-5"><StateLabel type="progress" text="In Progress" /></td>
                   </tr>
                   <tr className="hover:bg-steel-50 transition-colors">
                     <td className="px-6 py-5 font-bold text-steel-900">Shopping Cart</td>
                     <td className="px-6 py-5 text-steel-500 italic">"Awaiting procurement approval..."</td>
                     <td className="px-6 py-5"><StateLabel type="info" text="Action Required" /></td>
                   </tr>
                   <tr className="hover:bg-steel-50 transition-colors">
                     <td className="px-6 py-5 font-bold text-steel-900">API Gateway</td>
                     <td className="px-6 py-5 text-steel-500 italic">"External Hub Sync failed (503)..."</td>
                     <td className="px-6 py-5"><StateLabel type="error" text="Critical" /></td>
                   </tr>
                 </tbody>
               </table>
            </div>
          </section>
        </div>
      </div>

      <footer className="pt-16 border-t border-steel-200 flex flex-col items-center gap-6">
        <div className="flex items-center gap-4 text-steel-400 text-[9px] font-mono uppercase tracking-[0.42em]">
          <span className="w-1.5 h-1.5 rounded-full bg-steel-200"></span>
          System End Transmission
          <span className="w-1.5 h-1.5 rounded-full bg-steel-200"></span>
        </div>
        <div className="text-center space-y-1">
          <p className="text-[10px] text-steel-500 font-bold uppercase tracking-widest">CookCircuit OS v2.4.1.a</p>
          <p className="text-[10px] text-steel-400 font-medium">Made by LingLink AI and DigLab AI</p>
        </div>
      </footer>
    </motion.div>
  );
};